import { ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import {
  confirmBdcDo,
  exportBdcShipment,
  getBdcPriceCheck,
  getBdcShipment,
  getCompanyProfile,
  issueBdcInvoice,
  listBdcPoInvoices,
  listBdcShipments,
  unconfirmBdcDo,
} from "../../../services";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { CompanyProfile } from "../../../types/company-profile.type";
import { BdcInvoiceSummary, BdcPriceCheck, BdcShipmentDetail, BdcShipmentRow } from "../../../types/bdc.type";
import { BdcApiError, issueFailureKind, readBdcError } from "../apiError";
import BranchChip from "../Branch/BranchChip";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranches } from "../Branch/useBdcBranches";
import { bdcQueueLink, bdcSettingsLink, bdcShipmentLink, NO_STORES_LABEL } from "../constants";
import { buildControlSheet } from "../controlSheet";
import { deliveredStores } from "../Delivery/delivery";
import DeliveryTab from "../Delivery/DeliveryTab";
import HistoryTab from "../Delivery/HistoryTab";
import ManualEditor from "../Delivery/ManualEditor";
import RouteSummaryView from "../Documents/RouteSummaryView";
import { baht, pcs, roundLabel, thaiDate, thaiDateTime } from "../format";
import CancelInvoiceDialog from "../Invoice/CancelInvoiceDialog";
import InvoiceTab from "../Invoice/InvoiceTab";
import { liveInvoice } from "../Invoice/invoiceNo";
import { BDC_STATUS_META, BDC_STATUS_ORDER, shipmentActions, statusStep } from "../status";
import StatusChip from "../StatusChip";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { useBlobExport } from "../useBlobExport";
import BulkTable from "./BulkTable";
import ControlSheetView, { controlSheetTitle } from "./ControlSheetView";
import InvoiceA4 from "./InvoiceA4";
import PrintPortal from "./PrintPortal";

type TabKey = "bulk" | "line" | "route" | "inv" | "history";
const TABS: { key: TabKey; label: string }[] = [
  { key: "bulk", label: "ยอดส่งต่อสินค้า" },
  { key: "line", label: "ใบคุมรายร้าน" },
  { key: "route", label: "ใบคุมสรุปสาย" },
  { key: "inv", label: "INV" },
  { key: "history", label: "ประวัติ" },
];
const isTabKey = (value: string | null): value is TabKey => TABS.some((t) => t.key === value);

type Confirm = "confirm_do" | "unconfirm_do" | "issue" | null;

function Field({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ fontSize: "0.875rem" }}>{children}</Box>
    </Box>
  );
}

/** 1 ร่าง · 2 ยืนยัน DO · 3 ออก INV · 4 รอนำส่ง 7-11 — the current step dark, the done ones ticked. */
export function StatusSteps({ status }: Readonly<{ status: string }>) {
  const step = statusStep(status);
  return (
    <Box
      data-testid="bdc-status-steps"
      sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.75, fontSize: "0.8125rem" }}
    >
      {BDC_STATUS_ORDER.map((key, i) => {
        const current = i === step;
        return (
          <Box
            key={key}
            aria-current={current ? "step" : undefined}
            sx={{
              borderRadius: "6px",
              px: 1.25,
              py: 1,
              bgcolor: current ? "#363A44" : "#FFFFFF",
              color: current ? "#FFFFFF" : i < step ? "#1E8E5A" : "#5A5F6B",
              border: current ? "1px solid #363A44" : "1px solid #E6E7EB",
            }}
          >
            {i < step ? "✓" : i + 1} {BDC_STATUS_META[key].step}
            {current && " · กำลังทำ"}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * BDC · รายละเอียด shipment — one PO through the 1.5 flow: ร่าง (adjust delivery: auto per product
 * or per store) → ยืนยัน DO (locks the quantities) → ออก INV (B… / TB…) → รอนำส่ง 7-11 (via ไฟล์ส่ง
 * 7-11). Tabs: ยอดส่งต่อสินค้า, ใบคุมรายร้าน (with the manual editor), ใบคุมสรุปสาย, INV (real PDF)
 * and ประวัติ. The control sheets print the delivered pieces.
 */
export default function BdcShipment() {
  const { poNumber = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabKey = isTabKey(tabParam) ? tabParam : "bulk";
  const role = useUserRole();
  const canAct = featureAccess.bdcDesk(role);

  const [detail, setDetail] = useState<BdcShipmentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [version, setVersion] = useState(0);
  const [siblings, setSiblings] = useState<BdcShipmentRow[]>([]);
  const [invoices, setInvoices] = useState<BdcInvoiceSummary[]>([]);
  const [priceCheck, setPriceCheck] = useState<BdcPriceCheck | null>(null);
  const [seller, setSeller] = useState<CompanyProfile | null>(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [printing, setPrinting] = useState<TabKey | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<{ title: string; failure: BdcApiError } | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const { busy, run } = useBlobExport();
  const { branches } = useBdcBranches();

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    setDetail(null);
    setInvoices([]);
    setPriceCheck(null);
    setEditing(false);
    setSearch("");
    setActionError(null);
  }, [poNumber]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    getBdcShipment(poNumber)
      .then(({ data: { data } }) => {
        if (!active) return;
        setDetail(data);
        setNotFound(!data);
      })
      .catch(() => {
        if (active) {
          setDetail(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    listBdcPoInvoices(poNumber)
      .then(({ data: { data } }) => {
        if (active) setInvoices(data?.rows ?? []);
      })
      .catch(() => {
        if (active) setInvoices([]);
      });
    getBdcPriceCheck(poNumber)
      .then(({ data: { data } }) => {
        if (active) setPriceCheck(data);
      })
      .catch(() => {
        if (active) setPriceCheck(null);
      });
    return () => {
      active = false;
    };
  }, [poNumber, version]);

  const header = detail?.header;

  // the other rounds of the same DC on the same order day, for quick switching
  const siblingKey = header ? `${header.order_date}|${header.dc_code}|${header.company_id}` : "";
  useEffect(() => {
    if (!siblingKey) return;
    const [orderDate, dcCode, companyId] = siblingKey.split("|");
    let active = true;
    listBdcShipments({
      order_date: orderDate,
      dc_code: dcCode,
      ...(companyId ? { company_id: companyId } : {}),
      page: 1,
      limit: 20,
    })
      .then(({ data: { data } }) => {
        if (active) setSiblings(data?.rows ?? []);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [siblingKey, version]);

  useEffect(() => {
    getCompanyProfile()
      .then(({ data: { data } }) => setSeller(data))
      .catch(() => undefined);
  }, []);

  const stores = useMemo(() => (detail ? deliveredStores(detail.stores) : []), [detail]);
  const sheet = useMemo(
    () => (detail ? buildControlSheet(detail.items, stores, deferredSearch) : null),
    [detail, stores, deferredSearch],
  );
  const fullSheet = useMemo(() => (detail ? buildControlSheet(detail.items, stores) : null), [detail, stores]);
  const finishPrint = useCallback(() => setPrinting(null), []);

  const setTab = (next: TabKey) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  };

  const runAction = async () => {
    if (!header || !confirm) return;
    setActing(true);
    setActionError(null);
    const titles = { confirm_do: "ยืนยัน DO ไม่สำเร็จ", unconfirm_do: "ยกเลิกยืนยันไม่สำเร็จ", issue: "ออก INV ไม่สำเร็จ" };
    try {
      if (confirm === "confirm_do") await confirmBdcDo(header.po_number);
      else if (confirm === "unconfirm_do") await unconfirmBdcDo(header.po_number);
      else {
        await issueBdcInvoice(header.po_number);
        setTab("inv");
      }
    } catch (e) {
      const failure = readBdcError(e, titles[confirm]);
      // unconfirm reports through the global toast; the other two own their error here
      if (confirm !== "unconfirm_do") setActionError({ title: titles[confirm], failure });
    } finally {
      setActing(false);
      setConfirm(null);
      setEditing(false);
      reload();
    }
  };

  if (loading && !detail) return <LinearProgress sx={{ mt: 2 }} />;
  if (notFound || !detail || !header) {
    return (
      <Stack gap={2} sx={{ py: 2 }}>
        <Alert severity="warning">ไม่พบ shipment PO {poNumber}</Alert>
        <Box>
          <Button component={RouterLink} to={bdcQueueLink()} startIcon={<ArrowBackIcon />}>
            คิวงาน
          </Button>
        </Box>
      </Stack>
    );
  }

  const isX = header.kind === "X";
  const actions = shipmentActions(header.status, header.kind);
  const shipmentBranch = branches.filter((b) => b.company_id === header.company_id);
  const isTest = shipmentBranch.some((b) => b.is_test_mode);
  const live = liveInvoice(invoices);
  const exportType = tab === "line" || tab === "route" ? "control" : "bulk";
  const deliveryPieces = header.delivery_pieces ?? header.pieces;
  const printLabel: Record<TabKey, string> = {
    bulk: "สรุปสินค้า",
    line: "ใบคุมรายร้าน",
    route: "ใบคุมสรุปสาย",
    inv: "ใบส่งสินค้า A4",
    history: "",
  };
  const canPrint = tab !== "history" && !(isX && (tab === "line" || tab === "route")) && !(tab === "inv" && live);

  return (
    <Box sx={{ padding: "8px 0", ...TABLE_SX }}>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={1.5}>
        <Button variant="outlined" component={RouterLink} to={bdcQueueLink(header.order_date)} startIcon={<ArrowBackIcon />}>
          คิวงาน
        </Button>
        {siblings.length > 1 &&
          siblings.map((row) => (
            <Chip
              key={row.po_number}
              component={RouterLink}
              to={bdcShipmentLink(row.po_number, tab)}
              clickable
              label={row.kind === "X" ? NO_STORES_LABEL : `รอบ ${row.round ?? "-"}`}
              color={row.po_number === header.po_number ? "primary" : "default"}
              variant={row.po_number === header.po_number ? "filled" : "outlined"}
            />
          ))}
        <Box sx={{ flex: 1 }} />
        {canPrint && (
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => setPrinting(tab)}>
            พิมพ์{printLabel[tab]}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={exportType === "control" && isX}
          loading={busy === exportType}
          onClick={() =>
            run(exportType, () => exportBdcShipment(header.po_number, exportType), `bdc-${header.po_number}-${exportType}.xlsx`)
          }
        >
          Excel ({exportType === "control" ? "ใบคุม" : "สรุปสินค้า"})
        </Button>
        {canAct && actions.canUnconfirmDo && (
          <Button variant="outlined" color="warning" onClick={() => setConfirm("unconfirm_do")}>
            ยกเลิกยืนยัน
          </Button>
        )}
        {canAct && actions.canCancelInvoice && live && (
          <Button variant="outlined" color="error" onClick={() => setCancelling(live.invoice_no)}>
            ยกเลิก INV
          </Button>
        )}
        {canAct && actions.canConfirmDo && (
          <Button variant="contained" onClick={() => setConfirm("confirm_do")} disabled={editing}>
            ยืนยัน DO
          </Button>
        )}
        {canAct && actions.canIssueInvoice && (
          <Button variant="contained" onClick={() => setConfirm("issue")}>
            ออก INV{isTest ? " (ทดสอบ TB…)" : ""}
          </Button>
        )}
      </Stack>

      {isTest && (
        <Box mb={1.5}>
          <TestModeBanner branches={shipmentBranch} />
        </Box>
      )}

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          onClose={() => setActionError(null)}
          data-testid="bdc-action-error"
        >
          <AlertTitle>{actionError.title}</AlertTitle>
          {actionError.failure.message}
          {actionError.failure.invoiceNo && ` (เลข ${actionError.failure.invoiceNo})`}
          {issueFailureKind(actionError.failure) === "sequence" && (
            <Box mt={1}>
              <Button size="small" variant="outlined" component={RouterLink} to={bdcSettingsLink("invoice")}>
                ไปที่ ตั้งค่า BDC › เลขเอกสาร
              </Button>
            </Box>
          )}
          {issueFailureKind(actionError.failure) === "reload" && (
            <Typography variant="body2" mt={0.5}>
              โหลดข้อมูล PO ใหม่แล้ว — ตรวจสถานะด้านล่างก่อนทำต่อ
            </Typography>
          )}
        </Alert>
      )}

      <Card sx={{ ...CARD_SX, mb: 1.5 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
            <BranchChip branches={branches} companyId={header.company_id} branchCode={header.branch_code} />
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
              {header.dc_code} · {header.dc_name} · {isX ? `PO ${NO_STORES_LABEL}` : roundLabel(header.kind, header.round)} · PO{" "}
              {header.po_number}
            </Typography>
            <StatusChip status={header.status} />
            {header.is_d_plus_1 && <Tag tone="info">ต่างจังหวัด ส่ง D+1</Tag>}
            {live && <Tag tone="ok">INV {live.invoice_no}</Tag>}
            {priceCheck && priceCheck.warning_count > 0 && (
              <Tag tone="warn">ราคาไม่ตรงระบบ {priceCheck.warning_count} รายการ</Tag>
            )}
          </Stack>
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "8px 24px",
            }}
          >
            <Field label="วันที่สั่ง → วันส่ง">
              {thaiDate(header.order_date)} → <b>{thaiDate(header.delivery_date)}</b>
            </Field>
            <Field label="ร้าน / สาย">
              {isX ? "— (ไม่มี PICKLST)" : `${pcs(header.store_count)} ร้าน · ${pcs(header.line_count)} สาย`}
            </Field>
            <Field label="ชิ้น สั่ง → ส่ง">
              {pcs(header.pieces)} → <b>{pcs(deliveryPieces)}</b>
              {deliveryPieces !== header.pieces && <span className="sub"> ({pcs(deliveryPieces - header.pieces)})</span>}
            </Field>
            <Field label="ยอดก่อน VAT / VAT 7% (ตาม PO)">
              {baht(header.subtotal)} / {baht(header.vat)}
            </Field>
            <Field label="ยอดรวม (ตาม PO)">
              <b>฿{baht(header.total)}</b>
            </Field>
            <Field label="ยืนยัน DO">
              {header.do_confirmed_at ? `${header.do_confirmed_by_name ?? ""} ${thaiDateTime(header.do_confirmed_at)}` : "-"}
            </Field>
            <Field label="ผู้ซื้อ">{header.buyer_name ?? "-"}</Field>
            <Field label="นำเข้าเมื่อ">{thaiDateTime(header.imported_at)}</Field>
          </Box>
        </Box>
      </Card>

      <Box mb={1.5}>
        <StatusSteps status={header.status} />
      </Box>

      <Tabs value={tab} onChange={(_e, value: TabKey) => setTab(value)} sx={{ mb: 1.5, borderBottom: "1px solid #E3E5EA" }}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {tab === "bulk" && <DeliveryTab detail={detail} editable={canAct && actions.canEditDelivery} onSaved={reload} />}

      {tab === "line" &&
        (isX || !sheet ? (
          <Card sx={CARD_SX}>
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography className="sub">PO นี้{NO_STORES_LABEL} จึงไม่มีใบคุมรายร้าน</Typography>
            </Box>
          </Card>
        ) : editing ? (
          <ManualEditor
            detail={detail}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              reload();
            }}
          />
        ) : (
          <Card sx={CARD_SX}>
            <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1.25 }}>
              <TextField
                size="small"
                placeholder="ค้นหา สาย / รหัสร้าน / ชื่อร้าน"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 280 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Typography className="sub">
                ยอดส่ง · เรียง สาย → จุดส่ง · รวมย่อยต่อสาย · แสดงเฉพาะสินค้าที่มียอด ({sheet.columns.length}/{detail.items.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              {canAct && actions.canEditDelivery && (
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                  แก้ยอดส่งรายร้าน
                </Button>
              )}
            </Stack>
            <Typography sx={{ px: 2, pb: 1, fontWeight: 600, fontSize: "0.875rem" }}>{controlSheetTitle(header)}</Typography>
            <Box sx={{ ...SCROLL_SX, maxHeight: "calc(100vh - 260px)" }}>
              <ControlSheetView sheet={sheet} header={header} />
            </Box>
          </Card>
        ))}

      {tab === "route" && (
        <Card sx={{ ...CARD_SX, p: 2 }}>
          {isX || !fullSheet ? (
            <Typography className="sub" sx={{ textAlign: "center", p: 2 }}>
              PO นี้{NO_STORES_LABEL} จึงไม่มีใบคุมสรุปสาย
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <RouteSummaryView sheet={fullSheet} header={header} sellerName={seller?.name_th} />
            </Box>
          )}
        </Card>
      )}

      {tab === "inv" && (
        <InvoiceTab
          detail={detail}
          invoices={invoices}
          priceCheck={priceCheck}
          seller={seller}
          canCancel={canAct && actions.canCancelInvoice}
          onCancel={setCancelling}
        />
      )}

      {tab === "history" && <HistoryTab header={header} invoices={invoices} version={version} />}

      <Dialog open={confirm !== null} onClose={acting ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirm === "confirm_do" ? "ยืนยัน DO" : confirm === "unconfirm_do" ? "ยกเลิกยืนยัน DO" : "ออก INV"} · PO {header.po_number}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirm === "confirm_do" && (
              <>
                ยอดส่ง {pcs(deliveryPieces)} ชิ้น จะถูกล็อก แก้ไม่ได้จนกว่าจะยกเลิกยืนยัน · นำเข้าไฟล์ PO นี้ซ้ำหลังจากนี้จะไม่เขียนทับ
              </>
            )}
            {confirm === "unconfirm_do" && <>PO กลับเป็นร่าง แก้ยอดส่งได้อีกครั้ง</>}
            {confirm === "issue" && (
              <>
                ออก INV ต้นฉบับตามยอดส่ง {pcs(deliveryPieces)} ชิ้น {isTest ? "เลขชุดทดสอบ TB…" : "เลขชุด B…"} · ยกเลิกได้ภายหลัง
                (ต้องใส่เหตุผล เลขเดิมเป็น "ยกเลิก")
                {priceCheck && priceCheck.warning_count > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    ราคาใน PO ไม่ตรงกับราคาในระบบ {priceCheck.warning_count} รายการ — INV ใช้ราคาตาม PO
                  </Alert>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={acting}>
            ยกเลิก
          </Button>
          <Button variant="contained" color={confirm === "unconfirm_do" ? "warning" : "primary"} loading={acting} onClick={runAction}>
            {confirm === "confirm_do" ? "ยืนยัน DO" : confirm === "unconfirm_do" ? "ยกเลิกยืนยัน" : "ออก INV"}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelInvoiceDialog
        invoiceNo={cancelling}
        poNumber={header.po_number}
        onClose={() => setCancelling(null)}
        onCancelled={() => {
          setCancelling(null);
          reload();
        }}
      />

      {printing && (
        <PrintPortal orientation={printing === "line" || printing === "route" ? "landscape" : "portrait"} onDone={finishPrint}>
          {printing === "line" && fullSheet && <ControlSheetView sheet={fullSheet} header={header} print />}
          {printing === "route" && fullSheet && (
            <RouteSummaryView sheet={fullSheet} header={header} sellerName={seller?.name_th} />
          )}
          {printing === "inv" && <InvoiceA4 detail={detail} seller={seller} />}
          {printing === "bulk" && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: "12px", mb: 1 }}>
                สรุปสินค้า · {header.dc_code} {header.dc_name} · {roundLabel(header.kind, header.round)} · ส่ง{" "}
                {thaiDate(header.delivery_date)} · PO {header.po_number}
              </Typography>
              <BulkTable detail={detail} />
            </>
          )}
        </PrintPortal>
      )}
    </Box>
  );
}

